import { fetchPostcodeSignal } from "@Api/Customers/tenders";
import Card from "@Components/Card";
import HoverTooltip from "@Components/HoverTooltip";
import InformationIcon from "@Components/Icons/Information";
import OfcomLogo from "@Components/Icons/Ofcom";
import InfoPopup from "@Components/InfoPopup";
import { ToastStatus } from "@Components/Toast";
import { getIconFromNetwork } from "@Helpers/Network";
import { Quality, } from "@Shared/Entities/Customer/Tender/enums";
import { displayEnum } from "@Shared/Helpers/DisplayEnum";
import { getNetworkFromPlatform } from "@Shared/Helpers/Network";
import { validatePostcode } from "@Shared/Validate";
import dayjs from "dayjs";
import { fix } from "postcode";
import React, { useEffect, useState } from "react";
import styles from "./index.module.scss";
const version = 1;
const signalDataKey = "postcodes_signal_data";
const getSignalData = () => {
    const postcodeSignalData = localStorage.getItem(signalDataKey);
    if (postcodeSignalData) {
        const signalDataObject = JSON.parse(postcodeSignalData);
        return signalDataObject;
    }
    return {};
};
export default (props) => {
    const postcodesListKey = `signal_postcodes_${props.tender.id}`;
    const getSignalPostcodes = (importantPostcodes) => {
        const postcodeList = localStorage.getItem(postcodesListKey);
        if (postcodeList) {
            const postcodeArray = JSON.parse(postcodeList);
            return [...new Set([...importantPostcodes, ...postcodeArray])];
        }
        return [...new Set(importantPostcodes)];
    };
    const [signalData, setSignalData] = useState(() => getSignalData());
    const [postcodesList, setPostcodes] = useState(() => getSignalPostcodes(Object.keys(props.tender.ofcom)));
    const [searchValue, setSearchValue] = useState("");
    const [isValidPostcode, setValidPostCode] = useState(null);
    useEffect(() => {
        fetchSignalRatings(postcodesList);
        localStorage.setItem(postcodesListKey, JSON.stringify(postcodesList));
    }, [postcodesList]);
    useEffect(() => {
        setPostcodes(getSignalPostcodes(Object.keys(props.tender.ofcom)));
    }, [props.open]);
    const onChangeSearch = (event) => {
        if (!event.target.value) {
            setValidPostCode(null);
        }
        setSearchValue(event.target.value);
    };
    const onKeyDown = (event) => {
        if (event.key === "Enter") {
            event.preventDefault();
            const isValidPostcode = validatePostcode(searchValue);
            setValidPostCode(isValidPostcode);
            if (isValidPostcode) {
                const postCodes = [
                    ...new Set([...postcodesList, fix(searchValue)]),
                ];
                setPostcodes(postCodes);
            }
        }
    };
    const fetchSignalRatings = async (postcodes) => {
        if (Object.keys(props.tender.ofcom).length === 0) {
            return;
        }
        const signalDataObject = { ...signalData };
        const fetchNeeded = [];
        for (const postcode of postcodes) {
            const dataForPostcode = signalDataObject[postcode];
            if (!dataForPostcode ||
                !dataForPostcode.expiry ||
                dayjs(dataForPostcode.expiry).isBefore() ||
                !dataForPostcode.version ||
                dataForPostcode.version < version) {
                fetchNeeded.push(postcode);
            }
        }
        const fetchedData = await Promise.all(fetchNeeded.map((postcode) => fetchPostcodeSignal(props.tender, postcode).catch((error) => {
            if (error.response?.status === 404) {
                // props.notify({
                //     title: "Oops",
                //     description: error.response.data.message,
                //     status: ToastStatus.Error,
                // })
                setValidPostCode(false);
                return null;
            }
            props.notify({
                title: "Oops",
                description: `Couldn't get signal data for: ${postcode}`,
                status: ToastStatus.Error,
            });
            console.log(error);
            return null;
        })));
        for (const [index, data] of fetchedData.entries()) {
            if (data === null) {
                setPostcodes(postcodesList.filter((postcode) => postcode !== fetchNeeded[index]));
            }
            if (data) {
                setSearchValue("");
                signalDataObject[fetchNeeded[index]] = {
                    ...data,
                    expiry: dayjs().add(1, "month"),
                    version,
                };
            }
        }
        setSignalData(signalDataObject);
        localStorage.setItem(signalDataKey, JSON.stringify(signalDataObject));
    };
    const networkValues = props.tariffComponents.map((tc) => tc.networkComponent);
    const networkKeysSet = new Set(networkValues.map((value) => getNetworkFromPlatform(value)));
    const showQualityKey = () => {
        const qualities = [
            Quality["Poor to none (outdoor only)"],
            Quality["Variable (outdoor only)"],
            Quality["Good (outdoor only)"],
            Quality["Variable in-home, good outdoor"],
            Quality["Good in-home and outdoor"],
        ];
        return (React.createElement("div", { className: styles.scaleWrapper },
            React.createElement("div", { className: styles.scaleTitle },
                "Signal ratings by postcode (Powered by Ofcom)",
                React.createElement(HoverTooltip, { icon: React.createElement(InformationIcon, null), style: { maxWidth: "40rem" } }, "The data displayed on mobile coverage availability uses source data made available by Ofcom which is based on data from the mobile operators about how strong they think signal levels are at every location in the UK. Each mobile operator has a slightly different approach to displaying coverage on its own map, including assumptions on the handsets used, levels of call reliability and the expected signal loss when indoors or in car. Because Ofcom brings all mobile operator data together in a single place and holds it to a single, independent standard, the mobile coverage availability information above may display different levels of coverage than those seen on the operators' websites. We would therefore recommend you also check the mobile provider's coverage checker. Mobile coverage data is created by coverage modelling, and since this is based on computer predictions is not error free. Ofcom update the source data quarterly and the mobile network operators update theirs, but there may be times when the maps are based on slightly different data and therefore show different coverage.")),
            React.createElement("div", { className: styles.scale }, qualities.map((quality) => (React.createElement("div", { className: `${styles.quality} ${getQualityClass(quality)}`, key: quality },
                React.createElement("div", { className: styles.signals }, [0, 1, 2, 3, 4].map((value) => (React.createElement("div", { key: value, className: `${styles.signal} ${quality && quality >= value
                        ? styles.signalActive
                        : ""}` })))),
                displayEnum(Quality, quality)))))));
    };
    const getQualityClass = (quality) => {
        switch (quality) {
            case Quality["Good in-home and outdoor"]:
                return styles.veryGood;
            case Quality["Variable in-home, good outdoor"]:
                return styles.good;
            case Quality["Good (outdoor only)"]:
                return styles.medium;
            case Quality["Variable (outdoor only)"]:
                return styles.bad;
            case Quality["Poor to none (outdoor only)"]:
                return styles.veryBad;
        }
    };
    const displaySignalRatings = () => {
        const getSignalRatings = (ofcom) => Object.entries(ofcom).reduce((object, [networkKey, qualityData]) => {
            const network = Number(networkKey);
            if (!networkKeysSet.has(network)) {
                return object;
            }
            object[network] = {
                voice: qualityData,
                data: qualityData,
            };
            return object;
        }, {});
        const postcodeSignalRatings = {
            ...Object.entries(props.tender.ofcom).reduce((object, [postcode, ofcomRating]) => {
                object[postcode] = getSignalRatings(ofcomRating);
                return object;
            }, {}),
            ...Object.entries(signalData).reduce((object, [postcode, networkQuality]) => {
                object[fix(postcode)] = getSignalRatings(networkQuality);
                return object;
            }, {}),
        };
        const convertedSignalRatings = Object.entries(postcodeSignalRatings).reduce((object, [postcode, signalRatings]) => {
            for (const [networkKey, networkQuality] of Object.entries(signalRatings)) {
                const network = Number(networkKey);
                object[network] = {
                    ...object[network],
                    [postcode]: networkQuality,
                };
            }
            return object;
        }, {});
        const displayQuality = (quality) => {
            return (React.createElement("div", { className: styles.qualities },
                React.createElement("div", { className: `${styles.quality} ${styles.qualityBorder} ${getQualityClass(quality)}`, key: quality },
                    React.createElement("div", { className: styles.signals }, [0, 1, 2, 3, 4].map((value) => (React.createElement("div", { key: value, className: `${styles.signal} ${quality && quality >= value
                            ? styles.signalActive
                            : ""}` })))),
                    quality === undefined ? (React.createElement("div", { className: styles.qualityLoading }, "Loading...")) : (React.createElement("div", { className: styles.qualityText }, displayEnum(Quality, quality))))));
        };
        return Object.entries(convertedSignalRatings).map(([network, postcodesObject]) => {
            const postcodes = postcodesList.map((postcode) => {
                const { voice, data } = postcodesObject[postcode] || {};
                return (React.createElement("div", { className: styles.postcodeQualities, key: `${postcode}-${voice}-${data}` },
                    React.createElement("div", { className: styles.postcode }, fix(postcode)),
                    displayQuality(data),
                    React.createElement("div", { className: styles.removePostcode, onClick: () => {
                            setPostcodes(postcodesList.filter((p) => p !== postcode));
                        } }, "\u2715")));
            });
            return (React.createElement("div", { className: styles.network, key: network },
                React.createElement("h2", { className: styles.networkHeader }, getIconFromNetwork(Number(network))),
                postcodes?.[0] ? (React.createElement("div", { className: styles.postCodesGroup }, postcodes)) : (React.createElement("div", { className: styles.networkNoData }, "No postcodes added. Please enter one above."))));
        });
    };
    // const changePostcodes = (name: string, tags: string[]) => {
    //     const postcodeArray = [...new Set(tags)]
    //     setPostcodes(postcodeArray)
    // }
    // const formatPostcode = (text: string) => {
    //     if (validatePostcode(text) === true) {
    //         return fix(text)
    //     }
    // }
    const popup = () => {
        return (React.createElement(Card, { className: styles.card },
            React.createElement("div", { className: styles.header },
                React.createElement("div", { className: styles.mainTitle }, "Ofcom's Signal Rating"),
                React.createElement("div", { className: styles.exit, onClick: props.onClose }, "\u2715")),
            React.createElement("div", { className: styles.qualityKey }, showQualityKey()),
            !props.sharedComparison && (React.createElement("div", { className: styles.addPostcodes },
                React.createElement("div", { className: styles.label }, "Postcodes"),
                React.createElement("input", { className: styles.inputSearch, value: searchValue, onChange: onChangeSearch, onKeyDown: onKeyDown, placeholder: "Type and press enter" }),
                isValidPostcode === false && (React.createElement("div", { className: styles.invalidPostcode }, "Invalid postcode.")))),
            React.createElement("div", { className: styles.networkCoverage }, displaySignalRatings()),
            React.createElement("div", { className: styles.footer },
                React.createElement("div", { className: styles.ofcom },
                    React.createElement(OfcomLogo, null)))));
    };
    return (React.createElement(InfoPopup, { trigger: props.open, content: popup, onClose: props.onClose }));
};
